import { Map, Set } from 'immutable';

import { setItemSpecificsErrors, setProductDetailsErrors, setTaxonomyAttributesErrors } from '@/utils/product/errors';
import { shapeEbayDetailsForAPI, shapeTaxonomyAttributesForAPI } from '@/utils/product/shapeAttributes';
import { getEtsyTaxonomyAttribute } from '@/utils/bulkEdit/taxonomy';
import { getOversetAttributes } from '@/utils/product/taxonomy';
import { replaceOperation } from '@/utils/product/operations';
import { getPropertyType } from '@/utils/taxonomy/ebay/properties';
import { editDetails } from '@/utils/edit/details';
import { isTruthy } from '@/utils/bool';
import { getSize } from '@/utils/iterable/getSize';
import { get } from '@/utils/iterable/get';

import { AMOUNT, ERROR, OPERATIONS, TAXONOMY_ATTRIBUTE, VALUE } from '@/constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ATTRIBUTE } from '@/constants/attributes';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';
import { DATA } from '@/data';

export function details({ operation, product, ...rest }) {
  const channel = product.get('channel');
  const operationType = operation.get('type');

  switch (channel) {
    case EBAY: {
      switch (operationType) {
        case OPERATIONS[EBAY].CONDITION_ID: {
          const { shop, shopData } = rest;
          const value = Map({
            [ATTRIBUTE.DESCRIPTION]: product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, ATTRIBUTE.DESCRIPTION]),
            [VALUE]: operation.get('value'),
          });

          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.CONDITION]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[EBAY].CONDITION_DESCRIPTION: {
          const { shop, shopData } = rest;
          const value = Map({
            [ATTRIBUTE.DESCRIPTION]: operation.get('value'),
            [VALUE]: product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE]),
          });

          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.CONDITION]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[EBAY].ITEM_CATEGORY: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.ITEM_CATEGORY]: { category: value }});

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[EBAY].ITEM_SPECIFICS.UPDATE: {
          const { shop, shopData } = rest;
          const marketplaceId = shop.get('marketplaceId');
          const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
          let updatedProduct = product;

          for (const [name, operationValue] of operation.get('value').entries()) {
            const property = get([EBAY, marketplaceId, 'CATEGORIES', category, 'productDetails', name])(DATA);
            const source = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE]);
            const value = operationValue.get('value');

            if (!property || (getSize(value) === 0 && !source.has(name))) continue;

            const result = getSize(value) === 0
              ? source.delete(name)
              : source.has(name)
                ? source.setIn([name, 'value'], Set(value))
                : source.set(name,
                  Map({
                    name,
                    required: !!property.reqired,
                    type: getPropertyType(property),
                    value: Set(value),
                  })
                );

            updatedProduct = setItemSpecificsErrors({
              product: updatedProduct.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE], result),
              shop,
              shopData,
            });
          }

          if (!updatedProduct.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, ERROR])) {
            const itemSpecifics = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP);
            const value = shapeEbayDetailsForAPI({ details: itemSpecifics });

            return {
              ...rest,
              product: updatedProduct.set('operations',
                replaceOperation(
                  updatedProduct.get('operations', EMPTY.LIST),
                  Map({ type: OPERATIONS[EBAY].ITEM_SPECIFICS.SET, value })
                )
              ),
            };
          }

          return { product: updatedProduct, ...rest };
        }

        case OPERATIONS[EBAY].PRODUCT_DETAILS.UPDATE: {
          const { shop } = rest;
          const marketplaceId = shop.get('marketplaceId');
          const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
          let updatedProduct = product;

          for (const [name, operationValue] of operation.get('value').entries()) {
            const property = get([EBAY, marketplaceId, 'CATEGORIES', category, 'productDetails', name])(DATA);
            const source = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, VALUE]);
            const value = operationValue.get('value');

            if (!property || (getSize(value) === 0 && !source.has(name))) continue;

            const result = getSize(value) === 0
              ? source.delete(name)
              : source.has(name)
                ? source.setIn([name, 'value'], Set(value))
                : source.set(name,
                  Map({
                    name,
                    required: !!property.reqired,
                    type: getPropertyType(property),
                    value: Set(value),
                  })
                );

            updatedProduct = setProductDetailsErrors({
              product: updatedProduct.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, VALUE], result),
              shop,
            });

          }

          if (!updatedProduct.hasIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, ERROR])) {
            const productDetails = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP);
            const value = shapeEbayDetailsForAPI({ details: productDetails });

            return {
              ...rest,
              product: updatedProduct.set('operations',
                replaceOperation(
                  updatedProduct.get('operations', EMPTY.LIST),
                  Map({ type: OPERATIONS[EBAY].PRODUCT_DETAILS.SET, value })
                )
              ),
            };
          }

          return { product: updatedProduct, ...rest };
        }

        case OPERATIONS[EBAY].STORE_CATEGORY: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.STORE_CATEGORY]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        default: {
          return { product, ...rest };
        }
      }
    }

    case ETSY: {
      switch (operationType) {
        case OPERATIONS[ETSY].CATEGORY: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({
            product,
            shop,
            shopData,
            [ATTRIBUTE.CATEGORY]: { category: value || AMOUNT.ZERO },
          });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[ETSY].DIGITAL: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.DIGITAL]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };

        }

        case OPERATIONS[ETSY].IS_SUPPLY: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.IS_SUPPLY]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[ETSY].PRODUCTION_PARTNERS: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.PRODUCTION_PARTNERS]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[ETSY].SECTION: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({
            product,
            shop,
            shopData,
            [ATTRIBUTE.SECTION]: value === 'none'
              ? null
              : value,
          });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[ETSY].SHOULD_AUTO_RENEW: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.SHOULD_AUTO_RENEW]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES: {
          if (product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, ERROR])) {
            return { product, ...rest };
          }

          let updatedProduct = product;
          const category = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);
          const overset = getOversetAttributes(updatedProduct.get(FORM.VARIATIONS));

          for (const [key, operationValue] of operation.get('value').entries()) {
            const property = parseInt(key);

            if (overset.has(property)) continue;

            const scaleId = operationValue.get('scaleId');
            const values = operationValue.get('values');
            const valueIds = operationValue.get('valueIds');
            const attribute = getEtsyTaxonomyAttribute({ category, channel, property, scale: scaleId });
            let value = Map({ scaleId, values, valueIds });

            if (attribute.get('type') === TAXONOMY_ATTRIBUTE.SCALED) {
              if (scaleId === null || !attribute.hasIn(['scales', 'byId', scaleId])) continue;
            } else {
              if (getSize(valueIds) > 0) {
                value = value.set('valueIds', valueIds.filter(function filterValueIds(valueId) {
                  return attribute.hasIn(['options', 'byId', valueId]);
                }));

                if (getSize(value.get('valueIds')) > 0) {
                  if (getSize(value.get('valueIds')) !== getSize(valueIds)) {
                    value = value.set('values', valueIds.map(function getOptionLabel(valueId) {
                      return attribute.getIn(['options', 'byId', valueId, 'name']);
                    }));
                  }
                } else {
                  continue;
                }
              } else if (attribute.get('required')) {
                continue;
              }
            }

            updatedProduct = setTaxonomyAttributesErrors({
              product: updatedProduct.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES, property],
                Map({ scaleId, values, valueIds })
              ),
            });
          }

          const value = shapeTaxonomyAttributesForAPI({
            taxonomyAttributes: updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES]),
          });

          if (getSize(value) > 0) {
            return {
              ...rest,
              product: updatedProduct.set('operations',
                replaceOperation(
                  updatedProduct.get('operations', EMPTY.LIST),
                  Map({ type: OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES, value })
                )
              ),
            };
          }

          return { product, ...rest };
        }

        case OPERATIONS[ETSY].WHO_MADE: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.WHO_MADE]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[ETSY].WHEN_MADE: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.WHEN_MADE]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        default: {
          return { product, ...rest };
        }
      }
    }

    case FAIRE: {
      switch (operationType) {
        case OPERATIONS[FAIRE].CATEGORY: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.CATEGORY]: { category: value }});

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        default: {
          return { product, ...rest };
        }
      }
    }

    case SHOPIFY: {
      switch (operationType) {
        case OPERATIONS[SHOPIFY].CATEGORY: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.CATEGORY]: { category: value }});

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[SHOPIFY].COLLECTIONS: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({
            product,
            shop,
            shopData,
            [ATTRIBUTE.COLLECTIONS]: value.filter(isTruthy),
          });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[SHOPIFY].PRODUCT_TYPE: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.PRODUCT_TYPE]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        case OPERATIONS[SHOPIFY].VENDOR: {
          const { shop, shopData } = rest;
          const value = operation.get('value');
          const updates = editDetails({ product, shop, shopData, [ATTRIBUTE.VENDOR]: value });

          return {
            ...rest,
            product: updates.product.set('operations',
              updates.operations.reduce(replaceOperation, updates.product.get('operations', EMPTY.LIST))
            ),
          };
        }

        default: {
          return { product, ...rest };
        }
      }
    }

    default: {
      return { product, ...rest };
    }
  }
}
